<script lang="ts" setup>
import { useAuthStore } from '#modules/auth/store/auth';
import avatarPlaceholder from '@/assets/img/avatar-placeholder.svg';
import logoutIcon from '@/assets/img/icon/menu/logout.svg';
import { useMobileStore } from '@/store/mobile';
import type { SidebarMenu } from '@/types/SidebarMenu';
import { GUEST_ROUTES, getAuthRoutes } from '@/utils/constants/Menu';
import { storeToRefs } from 'pinia';
import { useDisplay } from 'vuetify';
import letterOpenedIcon from '~/assets/img/icon/letter-opened.svg';
import { AppAvatar, AppButton, AppTypography } from '~/components/base';
import { useNotificationsStore } from '~/store/notifications';

interface Props {
  prependMenu?: SidebarMenu[];
  appendMenu?: SidebarMenu[];
}

const { prependMenu = [], appendMenu = [] } = defineProps<Props>();
const emits = defineEmits<{
  (e: 'handleOpenLogoutDialog'): void;
}>();

const authStore = useAuthStore();
const { user } = storeToRefs(authStore);
// Access userRole directly from store to ensure reactivity
const userRole = computed(() => authStore.userRole);

// Initialize notifications store for unread indicator
const notificationsStore = useNotificationsStore();
const { notifications } = storeToRefs(notificationsStore);

// Check if the first notification is unread (for dot indicator)
const isFirstNotificationUnread = computed(() => {
  return notifications.value.length > 0 && notifications.value[0]?.read === false;
});

const { signOut } = useAuth();
const handleLogout = async () => {
  await signOut({ callbackUrl: '/login' });
  authStore.logout(); // Explicitly clear the store
};

// Detect large screen
const { lgAndUp } = useDisplay();

const config = useRuntimeConfig();
const navigations = computed((): SidebarMenu[] => {
  const menus = [];

  // Use the new reactive function for auth routes
  const authRoutes = getAuthRoutes(lgAndUp.value);
  const guestRoutes = [
    ...GUEST_ROUTES,
    {
      title: 'Contact Us',
      action: () => {
        // open to mailto:info@example.com
        window.location.href = 'mailto:' + config.public?.appContactUsEmail;
      },
      icon: letterOpenedIcon,
    },
  ];

  if (user.value && Object.keys(user.value).length) {
    menus.push(...authRoutes);
  } else {
    menus.push(...guestRoutes);
  }

  return [...prependMenu, ...menus, ...appendMenu] as SidebarMenu[];
});

// Filter out 'My Bids' for JOB_POSTER role
const filteredNavigations = computed(() => {
  return navigations.value.filter((navigation) => {
    // Hide 'My Bids' menu item for JOB_POSTER role
    if (navigation.title === 'My Bids' && userRole.value === 'JOB_POSTER') {
      return false;
    }
    return true;
  });
});

const logoutItem = {
  title: 'Logout',
  icon: logoutIcon,
};

const mobileStore = useMobileStore();
const { showMenu } = storeToRefs(mobileStore);

// Detect mobile screen
const { smAndDown } = useDisplay();

const closeMobileMenu = () => {
  if (!showMenu.value) {
    return;
  }

  mobileStore.toggleMenu(false);
  document.documentElement.style.overflow = 'auto';
};

const handleNavigationClick = async (navigation: SidebarMenu) => {
  if (!navigation.action) {
    await navigateTo(navigation.link || '/');
    closeMobileMenu();
    return;
  }

  try {
    await navigation.action();
  } finally {
    closeMobileMenu();
  }
};
</script>
<template>
  <div class="navbar-menu-wrapper">
    <!-- User Header (only for authenticated users) -->
    <div v-if="user" class="navbar-menu__header">
      <AppAvatar
        :src="user?.avatar_permanent_url || user?.avatar || avatarPlaceholder"
        :size="48"
        class="navbar-menu__avatar"
      />
      <div class="navbar-menu__user-info">
        <AppTypography element="div" type="h6" class="navbar-menu__user-name">
          {{ user?.full_name || 'User' }}
        </AppTypography>
        <AppTypography element="div" type="body-2" class="navbar-menu__user-email">
          {{ user?.email || '' }}
        </AppTypography>
      </div>
    </div>

    <div class="navbar-menu__divider"></div>

    <!-- Menu Items -->
    <div class="navbar-menu__items">
      <template v-for="(navigation, index) in filteredNavigations" :key="index">
        <div
          :data-cy="`menu-${navigation.link?.name}`"
          class="navbar-menu__item"
          @click="handleNavigationClick(navigation)"
        >
          <div v-if="navigation.icon" class="navbar-menu__icon-wrapper">
            <v-icon v-if="navigation.mdi" size="24" class="navbar-menu__icon">
              {{ navigation.icon }}
            </v-icon>
            <v-img v-else :src="navigation.icon" width="24" height="24" class="navbar-menu__icon"></v-img>
            <!-- Red dot indicator for Notifications menu item -->
            <span
              v-if="navigation.link?.name === 'notifications' && isFirstNotificationUnread"
              class="navbar-menu__notification-dot"
            ></span>
          </div>
          <AppTypography element="span" type="body-1" class="navbar-menu__text">
            {{ navigation.title }}
          </AppTypography>
        </div>
      </template>

      <!-- Logout Button (only for authenticated users) -->
      <div v-if="user" class="">
        <div class="navbar-menu__divider"></div>
        <div class="navbar-menu__item" @click="emits('handleOpenLogoutDialog')">
          <div class="navbar-menu__icon-wrapper">
            <v-img :src="logoutItem.icon" width="24" height="24" class="navbar-menu__icon"></v-img>
          </div>
          <AppTypography element="span" type="body-1" class="navbar-menu__text">
            {{ logoutItem.title }}
          </AppTypography>
        </div>
      </div>
    </div>

    <!-- Auth Buttons (only for guest users on mobile) -->
    <ClientOnly>
      <div v-if="!user && smAndDown" class="navbar-menu__auth-buttons">
        <div class="navbar-menu__auth-buttons-wrapper">
          <AppButton
            data-cy="menu-signup"
            class="navbar-menu__signup-button rounded-4xl"
            to="/register"
            @click="closeMobileMenu"
          >
            Sign Up
          </AppButton>
          <AppButton
            variant="outlined"
            data-cy="menu-login"
            class="navbar-menu__login-button rounded-4xl"
            to="/login"
            @click="closeMobileMenu"
          >
            Login
          </AppButton>
        </div>
      </div>
    </ClientOnly>

    <!-- Create Listing Button (only for authenticated users on mobile) -->
    <ClientOnly>
      <div v-if="user && smAndDown" class="navbar-menu__button-wrapper">
        <AppButton
          data-cy="menu-create-listing"
          class="navbar-menu__create-button"
          to="/jobs/create"
          @click="closeMobileMenu"
        >
          <v-icon class="navbar-menu__create-icon">mdi-plus</v-icon>
          Post a Listing
        </AppButton>
      </div>
    </ClientOnly>
  </div>
</template>
<style lang="scss" scoped>
@use '@/assets/scss/partials/colors' as *;

.navbar-menu-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: white;
  border-radius: 16px;
  overflow: hidden;

  @media (max-width: 768px) {
    border-radius: 0;
  }
}

// User Header Section
.navbar-menu__header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background-color: white;
}

.navbar-menu__avatar {
  flex-shrink: 0;
  border: 2px solid rgba(0, 0, 0, 0.08);
}

.navbar-menu__user-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
}

.navbar-menu__user-name {
  color: #000000;
  font-weight: 700;
  font-size: 20px;
  line-height: 1.5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: 'Big Shoulders', sans-serif;
}

.navbar-menu__user-email {
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
  line-height: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 400;
}

// Menu Items Section
.navbar-menu__items {
  flex: 1;
  padding: 8px 0;
}

.navbar-menu__item {
  display: flex;
  align-items: center;
  min-height: 64px;
  padding: 12px 24px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  position: relative;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  &--active {
    background-color: rgba(255, 223, 39, 0.1);

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background-color: #ffdf27;
    }
  }
}

.navbar-menu__icon-wrapper {
  margin-right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  position: relative;
}

.navbar-menu__icon {
  color: rgba(0, 0, 0, 0.6);
  transition: color 0.2s ease;
}

// Red dot indicator for notifications menu item
.navbar-menu__notification-dot {
  position: absolute;
  top: 0;
  right: -2px;
  width: 8px;
  height: 8px;
  background-color: #f10021;
  border-radius: 50%;
  border: 1.5px solid white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  pointer-events: none;
  z-index: 1;
}

.navbar-menu__icon {
  color: rgba(0, 0, 0, 0.6) !important;
}

.navbar-menu__item:hover .navbar-menu__icon {
  color: #414156;
}

.navbar-menu__item--active .navbar-menu__icon {
  color: #000000;
}

.navbar-menu__text {
  color: rgba(0, 0, 0, 0.87);
  font-size: 16px;
  line-height: 1.5;
  font-weight: 400;
}

.navbar-menu__item--active .navbar-menu__text {
  font-weight: 600;
  color: #000000;
}

// Logout Section
.navbar-menu__logout-section {
  margin-top: auto;
}

.navbar-menu__divider {
  height: 1px;
  background-color: rgba(0, 0, 0, 0.08);
  margin: 0px 24px;
}

.navbar-menu__logout-button {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 48px;
  padding: 12px 24px;
  background: none;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
  text-align: left;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  &:focus {
    outline: none;
    background-color: rgba(0, 0, 0, 0.04);
  }
}

.navbar-menu__logout-text {
  color: #01011d;
  font-size: 16px;
  line-height: 1.5;
  font-weight: 500;
}

.navbar-menu__logout-button:hover .navbar-menu__icon {
  color: #d32f2f;
}

// Auth Buttons Section (Guest users on mobile)
.navbar-menu__auth-buttons {
  margin-top: auto;
  padding: 16px 24px 24px;
  background-color: white;
}

.navbar-menu__auth-buttons-wrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.navbar-menu__login-button {
  width: 100%;
  height: 48px !important;
  background-color: transparent !important;
  color: #000000 !important;
  border: 2px solid #000000 !important;
  font-weight: 600 !important;
  font-size: 16px !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  box-shadow: none !important;

  &:hover {
    background-color: #000000 !important;
    color: #ffffff !important;
  }
}

.navbar-menu__signup-button {
  width: 100%;
  height: 48px !important;
  background-color: #ffdf27 !important;
  color: #01011d !important;
  border: 2px solid #ffdf27 !important;
  font-weight: 600 !important;
  font-size: 16px !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  box-shadow: none !important;

  &:hover {
    background-color: #e6c91f !important;
    border-color: #e6c91f !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 223, 39, 0.3) !important;
  }

  &:active {
    transform: translateY(0);
    box-shadow: none !important;
  }
}

// Create Listing Button (Mobile - Authenticated users)
.navbar-menu__button-wrapper {
  margin-top: auto;
  padding: 16px 24px 24px;
  background-color: white;
}

.navbar-menu__create-button {
  width: 100%;
  height: 48px !important;
  background-color: #ffdf27 !important;
  color: #01011d !important;
  border: 2px solid #ffdf27 !important;
  border-radius: 12px !important;
  font-weight: 600 !important;
  font-size: 16px !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  box-shadow: none !important;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background-color: #e6c91f !important;
    border-color: #e6c91f !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 223, 39, 0.3) !important;
  }

  &:active {
    transform: translateY(0);
    box-shadow: none !important;
  }
}

.navbar-menu__create-icon {
  font-size: 20px !important;
  margin: 0;
}
</style>
